import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { generateDemoRequestXml, generateXmlFilename } from '../services/xmlGenerator';
import { uploadXmlToBlobStorage, validateDomainId, getSasToken, simulateXmlUpload } from '../services/blobStorageService';
import { processDemoRequest } from '../services/zohoCrmService';
import { verifyCaptchaToken, isValidCaptchaToken } from '../services/captchaService';
import { handleImageErrorWithFallback } from '../utils/imageManager';
import Captcha from '../components/Captcha';
import RECAPTCHA_CONFIG from '../config/captchaConfig';

const ComparePrices = () => {
  const { brand, domainConfig } = useTheme();
  const { variant } = useVariant();
  const { currentSEO } = useSEO();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    vehicleType: [],
    vehicleCount: '',
    isBusiness: '',
    zipCode: '',
    companyName: '',
    fullName: '',
    email: '',
    phone: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef(null);

  const totalSteps = 8;

  // Handle form input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Move to next step
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Move to previous step
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate captcha
    if (!captchaToken) {
      setCaptchaError('Please complete the captcha verification');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Verify captcha token
      if (!isValidCaptchaToken(captchaToken)) {
        throw new Error('Invalid captcha token');
      }

      const captchaVerification = await verifyCaptchaToken(captchaToken);
      if (!captchaVerification.success) {
        throw new Error(captchaVerification.error || 'Captcha verification failed');
      }

      const domainId = brand?.id || 'default';
      const fullDomain = window.location.hostname || 'localhost';
      
      // Validate domain ID
      if (!validateDomainId(domainId)) {
        throw new Error('Invalid domain ID');
      }
      
      // Update createdOn timestamp and format data for submission
      const submitDateTime = new Date().toISOString();
      const submissionData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.companyName,
        fleetSize: formData.vehicleCount,
        notes: `Compare Dash Cam Prices Request:\nVehicle Types: ${formData.vehicleType.join(', ')}\nBusiness: ${formData.isBusiness}\nZIP Code: ${formData.zipCode}`,
        createdOn: submitDateTime,
        captchaToken: captchaToken
      };
      
      // Generate XML content
      const xmlContent = generateDemoRequestXml(submissionData, fullDomain);
      
      // Generate filename
      const filename = generateXmlFilename(domainId, submissionData.name, submitDateTime);
      
      // Try to get SAS token for authentication
      const sasToken = await getSasToken();
      
      // Upload to blob storage
      let uploadResult;
      if (sasToken) {
        uploadResult = await uploadXmlToBlobStorage(xmlContent, filename, domainId, sasToken);
      } else {
        uploadResult = await simulateXmlUpload(xmlContent, filename, domainId);
      }
      
      if (uploadResult.success) {
        // Process demo request through Zoho
        const zohoResult = await processDemoRequest(submissionData);
        
        if (zohoResult.success) {
          // Request processed successfully
        } else {
          // Continue with form submission even if Zoho fails
        }
        
        // Reset form after successful submission
        setFormData({
          vehicleType: [],
          vehicleCount: '',
          isBusiness: '',
          zipCode: '',
          companyName: '',
          fullName: '',
          email: '',
          phone: ''
        });
        
        // Reset captcha
        setCaptchaToken('');
        setCaptchaError('');
        if (captchaRef.current) {
          captchaRef.current.reset();
        }
        
        setCurrentStep(1);
        setSubmitError('');
        setIsSubmitted(true);
      } else {
        throw new Error(`Upload failed: ${uploadResult.error}`);
      }
    } catch (error) {
      setSubmitError(`Error submitting form: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          hero: 'bg-gray-50',
          heroTitle: 'text-gray-900',
          heroSubtitle: 'text-gray-900',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white border border-gray-200 shadow-lg',
          formCard: 'bg-white border-2 border-gray-300 shadow-xl',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          input: 'border-2 border-gray-300 focus:border-gray-900 focus:ring-gray-900',
          title: 'text-gray-900',
          text: 'text-gray-900',
          stepIndicator: 'bg-gray-200',
          stepActive: 'bg-gray-900',
          icon: 'text-gray-900'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          hero: 'bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          section: 'bg-transparent',
          sectionAlt: 'bg-white/5',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 shadow-xl',
          formCard: 'bg-white/10 backdrop-blur-md border-2 border-white/20 shadow-2xl',
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600',
          buttonSecondary: 'bg-white/10 text-white hover:bg-white/20',
          input: 'bg-white/10 border-2 border-white/20 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400',
          title: 'text-white',
          text: 'text-white',
          stepIndicator: 'bg-white/20',
          stepActive: 'bg-gradient-to-r from-purple-500 to-pink-500',
          icon: 'text-white'
        };
      case 'corporate':
        return {
          container: 'bg-gray-50',
          hero: 'bg-gradient-to-r from-blue-600 to-indigo-700',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white shadow-md',
          formCard: 'bg-white border-2 border-indigo-200 shadow-xl',
          button: 'bg-indigo-600 text-white hover:bg-indigo-700',
          buttonSecondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          input: 'border-2 border-gray-300 focus:border-indigo-600 focus:ring-indigo-600',
          title: 'text-gray-900',
          text: 'text-gray-900',
          stepIndicator: 'bg-gray-200',
          stepActive: 'bg-indigo-600',
          icon: 'text-gray-900'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          hero: 'bg-gradient-to-r from-gray-800 to-gray-900',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          section: 'bg-transparent',
          sectionAlt: 'bg-gray-800/50',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 shadow-xl',
          formCard: 'bg-white/10 backdrop-blur-md border-2 border-white/20 shadow-2xl',
          button: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700',
          buttonSecondary: 'bg-white/10 text-white hover:bg-white/20',
          input: 'bg-white/10 border-2 border-white/20 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400',
          title: 'text-white',
          text: 'text-white',
          stepIndicator: 'bg-white/20',
          stepActive: 'bg-gradient-to-r from-blue-600 to-purple-600',
          icon: 'text-white'
        };
      default:
        return {
          container: 'bg-white',
          hero: 'bg-blue-600',
          heroTitle: 'text-white',
          heroSubtitle: 'text-white',
          section: 'bg-white',
          sectionAlt: 'bg-gray-50',
          card: 'bg-white shadow-md',
          formCard: 'bg-white border-2 border-blue-200 shadow-xl',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          input: 'border-2 border-gray-300 focus:border-blue-600 focus:ring-blue-600',
          title: 'text-gray-900',
          text: 'text-gray-900',
          stepIndicator: 'bg-gray-200',
          stepActive: 'bg-blue-600',
          icon: 'text-gray-900'
        };
    }
  };

  const styles = getVariantStyles();

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              What type of business vehicle(s) do you need dash cams for?
            </h3>
            <p className={`text-sm mb-4 ${styles.text}`}>
              Select all that apply
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['Box Trucks / Semis', 'Pickup Trucks', 'Trucks - Other', 'Vans', 'Cars', 'Other'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    const currentTypes = formData.vehicleType;
                    const newTypes = currentTypes.includes(type)
                      ? currentTypes.filter(t => t !== type)
                      : [...currentTypes, type];
                    handleChange('vehicleType', newTypes);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    formData.vehicleType.includes(type)
                      ? `${styles.button}`
                      : `${styles.card} hover:border-current`
                  }`}
                >
                  <span className={`font-semibold ${formData.vehicleType.includes(type) ? 'text-white' : styles.text}`}>
                    {type}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              How many vehicles do you need dash cams for?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['1-9', '10-99', '100-999', '1000+'].map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => {
                    handleChange('vehicleCount', count);
                    nextStep();
                  }}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    formData.vehicleCount === count
                      ? `${styles.button}`
                      : `${styles.card} hover:border-current`
                  }`}
                >
                  <span className={`font-semibold ${formData.vehicleCount === count ? 'text-white' : styles.text}`}>
                    {count} vehicles
                  </span>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              Are you a business?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['Yes', 'No'].map((answer) => (
                <button
                  key={answer}
                  type="button"
                  onClick={() => {
                    handleChange('isBusiness', answer);
                    nextStep();
                  }}
                  className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                    formData.isBusiness === answer
                      ? `${styles.button}`
                      : `${styles.card} hover:border-current`
                  }`}
                >
                  <span className={`font-semibold text-xl ${formData.isBusiness === answer ? 'text-white' : styles.text}`}>
                    {answer}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              What's your ZIP code?
            </h3>
            <input
              type="text"
              value={formData.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              placeholder="Enter ZIP code"
              maxLength="10"
              className={`w-full px-4 py-3 rounded-lg ${styles.input} text-lg`}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              Company Name
            </h3>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="Enter company name"
              className={`w-full px-4 py-3 rounded-lg ${styles.input} text-lg`}
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              Full Name
            </h3>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleChange('fullName', e.target.value)}
              placeholder="Enter your full name"
              className={`w-full px-4 py-3 rounded-lg ${styles.input} text-lg`}
            />
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
              Email Address
            </h3>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter your email"
              className={`w-full px-4 py-3 rounded-lg ${styles.input} text-lg`}
            />
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className={`text-2xl font-bold mb-6 ${styles.title}`}>
                Phone Number
              </h3>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="Enter your phone number"
                className={`w-full px-4 py-3 rounded-lg ${styles.input} text-lg`}
              />
            </div>

            {/* Captcha */}
            <div className="mt-6">
              <Captcha
                ref={captchaRef}
                siteKey={RECAPTCHA_CONFIG.siteKey}
                theme={RECAPTCHA_CONFIG.theme}
                size={RECAPTCHA_CONFIG.size}
                onVerify={(token) => {
                  setCaptchaToken(token);
                  setCaptchaError('');
                }}
                onError={(error) => {
                  setCaptchaError(error);
                  setCaptchaToken('');
                }}
              />
              {captchaError && (
                <p className="mt-2 text-sm text-red-500">{captchaError}</p>
              )}
            </div>

            {submitError && (
              <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Check if current step is complete
  const isStepComplete = () => {
    switch (currentStep) {
      case 1:
        return formData.vehicleType.length > 0;
      case 2:
        return formData.vehicleCount !== '';
      case 3:
        return formData.isBusiness !== '';
      case 4:
        return formData.zipCode !== '';
      case 5:
        return formData.companyName !== '';
      case 6:
        return formData.fullName !== '';
      case 7:
        return formData.email !== '';
      case 8:
        return formData.phone !== '' && captchaToken !== '';
      default:
        return false;
    }
  };

  // Render success message within form
  const renderSuccessMessage = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100`}>
            <svg className={`h-8 w-8 text-green-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className={`mt-6 text-3xl font-extrabold ${styles.title}`}>
            Thanks for your request!
          </h2>
          <p className={`mt-4 text-lg ${styles.text}`}>
            We're reviewing your details to get you connected with the providers we've matched you with.
          </p>
        </div>

        <div className={`border-t pt-8 ${styles.text === 'text-white' ? 'border-white/20' : 'border-gray-200'}`}>
          <h3 className={`text-xl font-bold mb-4 ${styles.title}`}>
            What happens next?
          </h3>
          
          <div className="space-y-6">
            <div>
              <h4 className={`font-semibold mb-2 ${styles.title}`}>
                1. Check your email and expect a call:
              </h4>
              <p className={styles.text}>
                Providers usually get in touch by the next business day - or within the hour if it's during office hours.
              </p>
            </div>

            <div>
              <h4 className={`font-semibold mb-2 ${styles.title}`}>
                2. Free price quotes & consultation:
              </h4>
              <p className={styles.text}>
                Speak directly with providers to discuss your needs and get accurate pricing tailored to you.
              </p>
            </div>

            <div className={`mt-6 p-4 rounded-lg ${styles.text === 'text-white' ? 'bg-white/10' : 'bg-gray-50'}`}>
              <p className={`text-sm font-medium ${styles.text}`}>
                Please be aware that you may not receive quotes if:
              </p>
              <ul className={`mt-2 text-sm space-y-1 ${styles.text} list-disc list-inside`}>
                <li>The specifications you provided are not eligible for the provider's product/services</li>
                <li>There is an error in the contact details you provided, such as an invalid phone number</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <button 
              onClick={() => setIsSubmitted(false)} 
              className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${styles.button}`}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${styles.container}`}>

      {/* Hero Section */}
      {/* <section className={`${styles.hero} pt-32 pb-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className={`text-4xl lg:text-5xl font-bold mb-6 ${styles.heroTitle}`}>
            Save by Comparing Dash Cam Prices
          </h1>
          <p className={`text-xl ${styles.heroSubtitle}`}>
            Get free quotes from leading dash cam suppliers in minutes
          </p>
        </div>
      </section> */}

      {/* Form Section */}
      <section className={`py-16 ${styles.section}`}>
        <div className="text-center mb-12">
          <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
            Save by Comparing Dash Cam Prices
          </h2>
          <p className={`text-lg ${styles.subtitle}`}>
            Get free quotes from leading dash cam suppliers in minutes
          </p>
        </div>
        <div className="max-w-3xl mx-auto px-6">
          <div className={`rounded-2xl p-8 lg:p-12 ${styles.formCard}`}>
            {/* Progress Indicator */}
            {!isSubmitted && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-sm font-medium ${styles.text}`}>
                    Step {currentStep} of {totalSteps}
                  </span>
                  <span className={`text-sm font-medium ${styles.text}`}>
                    {Math.round(((currentStep - 1) / totalSteps) * 100)}% Complete
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full ${styles.stepIndicator}`}>
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${styles.stepActive}`}
                    style={{ width: `${((currentStep - 1) / totalSteps) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Form Steps or Success Message */}
            {isSubmitted ? (
              renderSuccessMessage()
            ) : (
              <form onSubmit={handleSubmit}>
                {renderStep()}

                {/* Navigation Buttons */}
                <div className="flex gap-4 mt-8">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${styles.buttonSecondary}`}
                    >
                      Previous
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!isStepComplete()}
                      className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!isStepComplete() || isSubmitting}
                      className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quotes'}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Our top Suppliers Section */}
      <section className={`py-20 ${styles.section}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
              Our top Suppliers
            </h2>
            <p className={`text-lg ${styles.subtitle}`}>
              We work with industry-leading suppliers to bring you the best prices
            </p>
          </div>
          <div className="flex justify-center">
            <img
              src={`/assets/domains/${brand.id}/suppliers.jpg`}
              alt="Our top Suppliers"
              className="w-full max-w-4xl rounded-2xl shadow-lg"
              onError={(e) => handleImageErrorWithFallback(e, brand.id, 'suppliers')}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={`py-20 ${styles.sectionAlt}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${styles.title}`}>
              Save Time & Money on Dash Cams in 3 Simple Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className={`rounded-xl p-8 ${styles.card}`}>
              <h1 className={`text-2xl font-bold mb-4 ${styles.icon}`}>
                1. Leave the hassle to us
              </h1>
              <p className={styles.text}>
                Complete our short form in under a minute
              </p>
            </div>

            {/* Step 2 */}
            <div className={`rounded-xl p-8 ${styles.card}`}>
              <h1 className={`text-2xl font-bold mb-4 ${styles.icon}`}>
                2. We're 100% free
              </h1>
              <p className={styles.text}>
                Receive free quotes from leading suppliers
              </p>
            </div>

            {/* Step 3 */}
            <div className={`rounded-xl p-8 ${styles.card}`}>
              <h1 className={`text-2xl font-bold mb-4 ${styles.icon}`}>
                3. Save time & money
              </h1>
              <p className={styles.text}>
                Save by comparing quotes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className={`py-16 ${styles.sectionAlt}`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className={`rounded-2xl p-8 lg:p-10 ${styles.card}`}>
            <h2 className={`text-2xl lg:text-3xl font-bold mb-6 text-center ${styles.title}`}>
              How do we work?
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className={`text-2xl font-bold flex-shrink-0 ${styles.icon}`}>1</span>
                <p className={`text-lg ${styles.text}`}>
                  We'll ask you a few questions about your business, so we can understand what you need.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className={`text-2xl font-bold flex-shrink-0 ${styles.icon}`}>2</span>
                <p className={`text-lg ${styles.text}`}>
                  We'll use your answers to match you with our top trusted suppliers. This is why we ask for your contact details. Your details are safe with us – we are fully GDPR compliant and we'll only ever match you with suppliers that we know can cater to your needs.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className={`text-2xl font-bold flex-shrink-0 ${styles.icon}`}>3</span>
                <p className={`text-lg ${styles.text}`}>
                  Your matched suppliers will get in touch with you, so you can ask questions and receive quotes tailored directly for your needs. You'll then be armed with all of the information you need to confidently compare and choose from.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparePrices;

